/**
 * One way to render a recovery duration, so the same object cannot read differently on two
 * screens.
 *
 * There were at least three of these: the canvas header badge, the node cards, and the Gantt —
 * each doing its own arithmetic on seconds or minutes. They disagreed. The badge dropped minutes
 * once the total passed a day, so OG-01's rolled-up RTO printed as "1d 23h" beside a Gantt
 * showing "47h 39m", and the two looked like different figures when both were 171,540 seconds.
 *
 * The database's *Text columns (CalculatedRTOText and the rest) are deliberately NOT used —
 * formatting is the application's job, and those columns are not part of how this product reads.
 */

/** Minutes → "1d 23h 39m". Days are shown once the total passes one, and minutes are never dropped. */
export function formatDurationMinutes(minutes: number | null | undefined): string {
  const m = Math.max(Math.round(Number(minutes ?? 0)), 0)
  if (m === 0) return '0m'
  const d = Math.floor(m / 1440)
  const h = Math.floor((m % 1440) / 60)
  const r = m % 60
  // Only the parts that carry information: a total of exactly two days reads "2d", not "2d 0h 0m".
  const parts: string[] = []
  if (d) parts.push(`${d}d`)
  if (h) parts.push(`${h}h`)
  if (r) parts.push(`${r}m`)
  return parts.join(' ')
}

/** Seconds → the same string. ordb stores EndToEndTime and TotalCalculatedRTO in seconds. */
export function formatDurationSeconds(seconds: number | null | undefined): string {
  return formatDurationMinutes(Math.round(Number(seconds ?? 0) / 60))
}

/**
 * PERSON-TIME, never elapsed time. Deliberately never rolls up into days.
 *
 * "2d 9h 30m" of engineer effort reads as a recovery that takes over two days — it is not, it is
 * 57½ hours of work that several people do at once. Rendering effort in hours keeps it visibly a
 * different KIND of quantity from a duration, which is exactly the confusion this caused on the
 * Resource overview: the effort figure was read against the RTO and the two did not reconcile.
 */
export function formatEffortMinutes(minutes: number): string {
  const m = Math.max(0, Math.round(minutes))
  const h = Math.floor(m / 60)
  const rem = m % 60
  if (h === 0) return `${rem}m`
  return rem === 0 ? `${h}h` : `${h}h ${rem}m`
}
